import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import basketApi from '../api/basketApi';

export const BasketContext = createContext(null);
const BASKET_STORAGE_KEY = 'nocturnal_basket';

// Helper to get stored basket
const getStoredBasket = () => {
  try {
    const stored = localStorage.getItem(BASKET_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (err) {
    console.error('Error reading basket from storage:', err);
    return null;
  }
};

// Helper to set stored basket
const setStoredBasket = (basket) => {
  try {
    if (basket) {
      localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(basket));
    } else {
      localStorage.removeItem(BASKET_STORAGE_KEY);
    }
  } catch (err) {
    console.error('Error saving basket to storage:', err);
  }
};

export const useBasket = () => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider');
  }
  return context;
};

export const BasketProvider = ({ children }) => {
  // Initialize state from localStorage or default
  const [basketState, setBasketState] = useState(() => 
    getStoredBasket() || {
      id: null,
      orderItems: [],
      total: 0,
      status: 'active',
      isPaymentAllowed: true
    }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to safely update basket state and storage
  const updateBasketState = useCallback((newBasket) => {
    if (newBasket && typeof newBasket === 'object') {
      setBasketState(newBasket);
      setStoredBasket(newBasket);
    }
  }, []);

  // Persist to localStorage when basket changes
  useEffect(() => {
    setStoredBasket(basketState);
  }, [basketState]);

  const refreshBasket = useCallback(() => {
    try {
      const freshBasket = getStoredBasket();
      if (freshBasket) {
        updateBasketState(freshBasket);
      }
      return true;
    } catch (err) {
      console.error('Error refreshing basket:', err);
      return false;
    }
  }, [updateBasketState]);

  const isProductInBasket = useCallback((productId) => {
    return basketState.orderItems.some(item => item.productId === productId);
  }, [basketState.orderItems]);

  const addToBasket = async (product, quantity = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await basketApi.addItem(product, basketState.id, quantity);
      
      if (response?.basket) {
        updateBasketState(response.basket);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error adding to basket:', err);
      setError(err.message || 'Failed to add item to basket');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromBasket = async (itemId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await basketApi.removeItem(itemId, basketState.id);
      if (response?.basket) {
        updateBasketState(response.basket);
        return true;
      }
      return false;
    } catch (err) {
      setError(err.message || 'Failed to remove item from basket');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    setLoading(true);
    setError(null);
    const previousState = basketState;

    try {
      if (quantity < 1) {
        throw new Error('Quantity must be at least 1');
      }

      const item = basketState.orderItems.find(item => item.id === itemId);
      if (!item) {
        throw new Error('Item not found in basket');
      }

      if (item.maxPerOrder && quantity > item.maxPerOrder) {
        throw new Error(`Maximum ${item.maxPerOrder} items allowed`);
      }

      if (item.remaining && quantity > item.remaining) {
        throw new Error(`Only ${item.remaining} items available`);
      }

      // Optimistically update the UI
      const updatedItems = basketState.orderItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      );

      updateBasketState({
        ...basketState,
        orderItems: updatedItems
      });

      // Make API call
      const response = await basketApi.updateQuantity(basketState.id, itemId, item.productId, quantity);
      
      if (response?.basket) {
        updateBasketState(response.basket);
        return true;
      }

      // If API call fails, rollback
      updateBasketState(previousState);
      return false;
    } catch (err) {
      // Rollback on error
      updateBasketState(previousState);
      setError(err.message || 'Failed to update quantity');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateBasket = useCallback((key, value) => {
    updateBasketState({
      ...basketState,
      [key]: value
    });
  }, [basketState, updateBasketState]);

  const emptyBasket = useCallback(() => {
    const emptyBasketState = {
      id: null,
      orderItems: [],
      total: 0,
      status: 'active',
      isPaymentAllowed: true
    };
    updateBasketState(emptyBasketState);
  }, [updateBasketState]);

  return (
    <BasketContext.Provider
      value={{
        basket: basketState,
        storedBasket: getStoredBasket(), // Keep for backward compatibility
        loading,
        error,
        addToBasket,
        removeFromBasket,
        emptyBasket,
        updateQuantity,
        isProductInBasket,
        refreshBasket,
        updateBasket,
        setBasketState: updateBasketState // Expose our safer function as setBasketState
      }}
    >
      {children}
    </BasketContext.Provider>
  );
};

export default BasketContext;
